// docs/116: what my client is doing in the inn, shared by the inn's layer on the stage (the hotspots,
// the people), the heroes the view draws (what each is doing: warming their hands, reading, holding a
// mug, asleep) and the panels over the view (the dice table close up, the noticeboard, the book, the
// window, the bar, the bard). Local to this client; what others see of it is my `village_presence.pose`.
import { create } from 'zustand';
import { POSE, type DrinkKind, type PoseCode } from './types';

export type InnPanel = 'dice' | 'notice' | 'guestbook' | 'window' | 'bar' | 'bard' | 'room';
/** What a hero of mine is doing (drawn on their figure by `HeroBits`). */
export type HeroAct = 'warm' | 'poke' | 'read' | 'gaze' | 'drink' | 'toast' | 'pet' | 'throw' | 'sleep' | 'cheer' | 'clap' | 'watch';

export interface InnState {
  panel?: InnPanel;
  /** Which window a gaze is at (`InnPlan.gaze` order); whether the dice panel has me seated or watching. */
  window: number;
  diceMode: 'seat' | 'watch';
  /** My band's pose (what my `village_presence` row says). */
  pose: PoseCode;
  /** Each of my heroes' (and, for a moment, anyone's) act, by `VillageHero.key`. */
  acts: Record<string, HeroAct | undefined>;
  /** The mug each hero holds (CI12), by hero key; anyone's (another player's drink slides to them too). */
  mugs: Record<string, DrinkKind | undefined>;
  /** CI8: the book my reader has open. */
  book?: { key: string; title: string };
  /** CI7: the tune the bard is playing now (set by `BardDirector`), for the room's clapping and its sound. */
  playing?: { tune: number; startedAtMs: number; by: string };
  /** Walk my band to one of the inn's places and do what it does there (`InnLayer` sets it; the panels' buttons call it):
   * 'hearth', 'armchair-0', 'window-0', 'notice', 'lectern', 'bard', 'bar', 'dice', 'stairs', 'bed-0'. */
  go?: (spot: string) => void;
  open(panel: InnPanel, opts?: { window?: number; diceMode?: 'seat' | 'watch' }): void;
  close(): void;
  setPose(pose: PoseCode): void;
  act(keys: readonly string[], act: HeroAct | undefined): void;
  /** Set an act for `ms`, then put back what was there. */
  flash(keys: readonly string[], act: HeroAct, ms: number): void;
  hold(key: string, drink: DrinkKind | undefined): void;
  setBook(book: InnState['book']): void;
  /** Leaving the inn: everything put down. */
  reset(): void;
}

export const useInn = create<InnState>()((set, get) => ({
  window: 0,
  diceMode: 'watch',
  pose: POSE.standing,
  acts: {},
  mugs: {},
  open: (panel, opts = {}) => set({ panel, ...(opts.window !== undefined ? { window: opts.window } : {}), ...(opts.diceMode ? { diceMode: opts.diceMode } : {}) }),
  close: () => set({ panel: undefined }),
  setPose: (pose) => set({ pose }),
  act: (keys, act) => set((s) => ({ acts: { ...s.acts, ...Object.fromEntries(keys.map((k) => [k, act])) } })),
  flash: (keys, act, ms) => {
    const was = Object.fromEntries(keys.map((k) => [k, get().acts[k]]));
    get().act(keys, act);
    window.setTimeout(() => set((s) => ({ acts: { ...s.acts, ...Object.fromEntries(keys.filter((k) => s.acts[k] === act).map((k) => [k, was[k]])) } })), ms);
  },
  hold: (key, drink) => set((s) => ({ mugs: { ...s.mugs, [key]: drink } })),
  setBook: (book) => set({ book }),
  reset: () => set({ panel: undefined, pose: POSE.standing, acts: {}, mugs: {}, book: undefined }),
}));
